import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { WorkspaceProvider } from '@/contexts/WorkspaceContext';
import { ToastProvider } from '@/components/Toast';
import ProtectedRoute from '@/components/ProtectedRoute';
import AppShell from '@/components/AppShell';
import CookieBanner from '@/components/CookieBanner';
import MarkLoader from '@/components/MarkLoader';
import { FEATURES } from '@/lib/featureFlags';

// Route-based code splitting — each page becomes its own webpack chunk.
// @react-pdf/renderer (~500KB) lands in the faktury/reporting chunks only,
// keeping the Dashboard/Nastavenia LCP path lean.
const Login = lazy(() => import('@/pages/Login'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Faktury = lazy(() => import('@/pages/Faktury'));
const Klienti = lazy(() => import('@/pages/Klienti'));
const KlientDetail = lazy(() => import('@/pages/KlientDetail'));
const Vydavky = lazy(() => import('@/pages/Vydavky'));
const Dokumenty = lazy(() => import('@/pages/Dokumenty'));
const Nastavenia = lazy(() => import('@/pages/Nastavenia'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const Design = lazy(() => import('@/pages/Design'));
const TemplateCheck = lazy(() => import('@/pages/TemplateCheck'));
const IconsPreview = lazy(() => import('@/pages/IconsPreview'));
const Reporting = lazy(() => import('@/pages/Reporting'));
const Polozky = lazy(() => import('@/pages/Polozky'));
const InvoicePrint = lazy(() => import('@/pages/InvoicePrint'));
const NewInvoiceScreen = lazy(() => import('@/components/modals/NewInvoiceScreen'));
const CenovePonuky = lazy(() => import('@/pages/CenovePonuky'));
const QuoteEditorScreen = lazy(() => import('@/components/modals/QuoteEditorScreen'));
const QuotePrint = lazy(() => import('@/pages/QuotePrint'));

const COOKIE_HIDE_PATHS = ['/login', '/auth/callback', '/privacy', '/terms'];

function BannerHost() {
  const location = useLocation();
  if (COOKIE_HIDE_PATHS.includes(location.pathname)) return null;
  return <CookieBanner />;
}

function RouteFallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <MarkLoader variant="pulse" size={32} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WorkspaceProvider>
          <ToastProvider>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/design" element={<Design />} />
              <Route path="/design/templates" element={<TemplateCheck />} />
              <Route path="/icons" element={<IconsPreview />} />
              <Route path="/faktury/:id/print-html" element={<ProtectedRoute><InvoicePrint /></ProtectedRoute>} />
              <Route path="/cenove-ponuky/:id/print" element={<ProtectedRoute><QuotePrint /></ProtectedRoute>} />

              {/* Onboarding — protected (must be logged in) but no workspace required */}
              <Route path="/onboarding" element={<ProtectedRoute requireWorkspace={false}><Onboarding /></ProtectedRoute>} />

              {/* Main app shell — protected + requires workspace */}
              <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/faktury" element={<Faktury />} />
                <Route path="/faktury/nova" element={<NewInvoiceScreen />} />
                <Route path="/faktury/:id/upravit" element={<NewInvoiceScreen />} />
                <Route path="/cenove-ponuky" element={<CenovePonuky />} />
                <Route path="/cenove-ponuky/nova" element={<QuoteEditorScreen />} />
                <Route path="/cenove-ponuky/:id/upravit" element={<QuoteEditorScreen />} />
                <Route path="/klienti" element={<Klienti />} />
                <Route path="/klienti/:id" element={<KlientDetail />} />
                <Route path="/vydavky" element={<Vydavky />} />
                <Route path="/polozky" element={<Polozky />} />
                {FEATURES.documents && <Route path="/dokumenty" element={<Dokumenty />} />}
                <Route path="/reporting" element={<Reporting />} />
                <Route path="/nastavenia" element={<Nastavenia />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            <BannerHost />
          </ToastProvider>
        </WorkspaceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
